import type { Exercise } from '@/engine/types';

/**
 * Catálogo semilla de ejercicios (SPEC 6.D).
 *
 * Cada entrada lleva los cinco datos que el motor necesita para razonar:
 * patrón de movimiento, músculo principal, secundarios, incremento mínimo de
 * carga y rango de repeticiones. Sin el patrón y el músculo principal no se
 * puede detectar redundancia; sin el incremento no se puede proponer subir peso.
 *
 * Incrementos: barra 2,5 kg (dos discos de 1,25), mancuernas 2 kg (el salto
 * habitual en un gimnasio español), máquinas y poleas 2,5 kg.
 */

const BARRA = 2.5;
const MANCUERNA = 2;
const MAQUINA = 2.5;

function ex(
  id: string,
  name: string,
  pattern: Exercise['pattern'],
  primary: Exercise['primary'],
  secondary: Exercise['secondary'],
  equipment: Exercise['equipment'],
  incrementKg: number,
  min: number,
  max: number,
): Exercise {
  return { id, name, pattern, primary, secondary, equipment, incrementKg, repRange: { min, max } };
}

export const EXERCISES: readonly Exercise[] = [
  // --- Empuje horizontal ---
  ex(
    'press-banca',
    'Press de banca',
    'empuje_horizontal',
    ['pectoral'],
    ['triceps', 'deltoides_anterior'],
    'barra',
    BARRA,
    5,
    8,
  ),
  ex(
    'press-banca-mancuernas',
    'Press de banca con mancuernas',
    'empuje_horizontal',
    ['pectoral'],
    ['triceps', 'deltoides_anterior'],
    'mancuernas',
    MANCUERNA,
    8,
    12,
  ),
  ex(
    'press-banca-declinado',
    'Press declinado',
    'empuje_horizontal',
    ['pectoral'],
    ['triceps'],
    'barra',
    BARRA,
    6,
    10,
  ),
  ex(
    'press-pecho-maquina',
    'Press de pecho en máquina',
    'empuje_horizontal',
    ['pectoral'],
    ['triceps', 'deltoides_anterior'],
    'maquina',
    MAQUINA,
    8,
    12,
  ),
  ex(
    'flexiones',
    'Flexiones',
    'empuje_horizontal',
    ['pectoral'],
    ['triceps', 'abdominales'],
    'peso_corporal',
    BARRA,
    8,
    20,
  ),
  ex(
    'fondos-paralelas',
    'Fondos en paralelas',
    'empuje_horizontal',
    ['pectoral'],
    ['triceps', 'deltoides_anterior'],
    'peso_corporal',
    BARRA,
    6,
    12,
  ),
  ex(
    'aperturas-mancuernas',
    'Aperturas con mancuernas',
    'aislamiento',
    ['pectoral'],
    ['deltoides_anterior'],
    'mancuernas',
    MANCUERNA,
    10,
    15,
  ),
  ex('cruces-polea', 'Cruces en polea', 'aislamiento', ['pectoral'], [], 'polea', MAQUINA, 10, 15),
  ex(
    'press-suelo',
    'Press en el suelo',
    'empuje_horizontal',
    ['pectoral'],
    ['triceps'],
    'barra',
    BARRA,
    6,
    10,
  ),

  // --- Empuje inclinado ---
  ex(
    'press-inclinado',
    'Press inclinado con barra',
    'empuje_inclinado',
    ['pectoral'],
    ['deltoides_anterior', 'triceps'],
    'barra',
    BARRA,
    6,
    10,
  ),
  ex(
    'press-inclinado-mancuernas',
    'Press inclinado con mancuernas',
    'empuje_inclinado',
    ['pectoral'],
    ['deltoides_anterior', 'triceps'],
    'mancuernas',
    MANCUERNA,
    8,
    12,
  ),
  ex(
    'press-inclinado-maquina',
    'Press inclinado en máquina',
    'empuje_inclinado',
    ['pectoral'],
    ['deltoides_anterior', 'triceps'],
    'maquina',
    MAQUINA,
    8,
    12,
  ),
  ex(
    'aperturas-inclinadas',
    'Aperturas inclinadas',
    'aislamiento',
    ['pectoral'],
    ['deltoides_anterior'],
    'mancuernas',
    MANCUERNA,
    10,
    15,
  ),

  // --- Empuje vertical ---
  ex(
    'press-militar',
    'Press militar',
    'empuje_vertical',
    ['deltoides_anterior'],
    ['triceps', 'deltoides_lateral'],
    'barra',
    BARRA,
    5,
    8,
  ),
  ex(
    'press-militar-mancuernas',
    'Press militar con mancuernas',
    'empuje_vertical',
    ['deltoides_anterior'],
    ['triceps', 'deltoides_lateral'],
    'mancuernas',
    MANCUERNA,
    8,
    12,
  ),
  ex(
    'press-arnold',
    'Press Arnold',
    'empuje_vertical',
    ['deltoides_anterior'],
    ['deltoides_lateral', 'triceps'],
    'mancuernas',
    MANCUERNA,
    8,
    12,
  ),
  ex(
    'press-hombro-maquina',
    'Press de hombro en máquina',
    'empuje_vertical',
    ['deltoides_anterior'],
    ['triceps'],
    'maquina',
    MAQUINA,
    8,
    12,
  ),
  ex(
    'push-press',
    'Push press',
    'empuje_vertical',
    ['deltoides_anterior'],
    ['triceps', 'cuadriceps'],
    'barra',
    BARRA,
    3,
    6,
  ),
  ex(
    'press-tras-nuca',
    'Press tras nuca',
    'empuje_vertical',
    ['deltoides_anterior'],
    ['deltoides_lateral', 'triceps'],
    'barra',
    BARRA,
    6,
    10,
  ),

  // --- Tracción horizontal ---
  ex(
    'remo-barra',
    'Remo con barra',
    'traccion_horizontal',
    ['dorsal'],
    ['biceps', 'trapecio', 'deltoides_posterior'],
    'barra',
    BARRA,
    6,
    10,
  ),
  ex(
    'remo-mancuerna',
    'Remo con mancuerna a una mano',
    'traccion_horizontal',
    ['dorsal'],
    ['biceps', 'trapecio'],
    'mancuernas',
    MANCUERNA,
    8,
    12,
  ),
  ex(
    'remo-polea-baja',
    'Remo en polea baja',
    'traccion_horizontal',
    ['dorsal'],
    ['biceps', 'trapecio'],
    'polea',
    MAQUINA,
    8,
    12,
  ),
  ex(
    'remo-maquina',
    'Remo en máquina',
    'traccion_horizontal',
    ['dorsal'],
    ['biceps', 'trapecio'],
    'maquina',
    MAQUINA,
    8,
    12,
  ),
  ex(
    'remo-t',
    'Remo en T',
    'traccion_horizontal',
    ['dorsal'],
    ['biceps', 'trapecio'],
    'barra',
    BARRA,
    8,
    12,
  ),
  ex(
    'remo-pendlay',
    'Remo Pendlay',
    'traccion_horizontal',
    ['dorsal'],
    ['biceps', 'trapecio'],
    'barra',
    BARRA,
    5,
    8,
  ),
  ex(
    'remo-invertido',
    'Remo invertido',
    'traccion_horizontal',
    ['dorsal'],
    ['biceps', 'deltoides_posterior'],
    'peso_corporal',
    BARRA,
    8,
    15,
  ),
  ex(
    'face-pull',
    'Face pull',
    'traccion_horizontal',
    ['deltoides_posterior'],
    ['trapecio'],
    'polea',
    MAQUINA,
    12,
    20,
  ),

  // --- Tracción vertical ---
  ex(
    'dominadas',
    'Dominadas',
    'traccion_vertical',
    ['dorsal'],
    ['biceps', 'antebrazo'],
    'peso_corporal',
    BARRA,
    5,
    10,
  ),
  ex(
    'dominadas-supinas',
    'Dominadas supinas',
    'traccion_vertical',
    ['dorsal'],
    ['biceps'],
    'peso_corporal',
    BARRA,
    5,
    10,
  ),
  {
    // El peso que se anota es la ASISTENCIA: progresar es quitarla.
    ...ex(
      'dominadas-asistidas',
      'Dominadas asistidas',
      'traccion_vertical',
      ['dorsal'],
      ['biceps'],
      'maquina',
      MAQUINA,
      8,
      12,
    ),
    assisted: true,
  },
  ex(
    'jalon-pecho',
    'Jalón al pecho',
    'traccion_vertical',
    ['dorsal'],
    ['biceps'],
    'polea',
    MAQUINA,
    8,
    12,
  ),
  ex(
    'jalon-neutro',
    'Jalón agarre neutro',
    'traccion_vertical',
    ['dorsal'],
    ['biceps'],
    'polea',
    MAQUINA,
    8,
    12,
  ),
  ex(
    'pullover-polea',
    'Pullover en polea',
    'aislamiento',
    ['dorsal'],
    ['triceps'],
    'polea',
    MAQUINA,
    10,
    15,
  ),

  // --- Sentadilla ---
  ex(
    'sentadilla-trasera',
    'Sentadilla trasera',
    'sentadilla',
    ['cuadriceps'],
    ['gluteo', 'lumbares', 'aductores'],
    'barra',
    BARRA,
    5,
    8,
  ),
  ex(
    'sentadilla-frontal',
    'Sentadilla frontal',
    'sentadilla',
    ['cuadriceps'],
    ['gluteo', 'abdominales'],
    'barra',
    BARRA,
    5,
    8,
  ),
  ex(
    'sentadilla-goblet',
    'Sentadilla goblet',
    'sentadilla',
    ['cuadriceps'],
    ['gluteo', 'abdominales'],
    'mancuernas',
    MANCUERNA,
    8,
    15,
  ),
  ex('prensa', 'Prensa de piernas', 'sentadilla', ['cuadriceps'], ['gluteo'], 'maquina', 5, 8, 15),
  ex(
    'hack-squat',
    'Sentadilla hack',
    'sentadilla',
    ['cuadriceps'],
    ['gluteo'],
    'maquina',
    MAQUINA,
    8,
    12,
  ),
  ex(
    'sentadilla-caja',
    'Sentadilla a cajón',
    'sentadilla',
    ['cuadriceps'],
    ['gluteo'],
    'barra',
    BARRA,
    5,
    8,
  ),
  ex(
    'sentadilla-sissy',
    'Sentadilla sissy',
    'sentadilla',
    ['cuadriceps'],
    [],
    'peso_corporal',
    MANCUERNA,
    10,
    15,
  ),

  // --- Bisagra de cadera ---
  ex(
    'peso-muerto',
    'Peso muerto',
    'bisagra',
    ['isquiosurales'],
    ['gluteo', 'lumbares', 'dorsal'],
    'barra',
    BARRA,
    3,
    6,
  ),
  ex(
    'peso-muerto-rumano',
    'Peso muerto rumano',
    'bisagra',
    ['isquiosurales'],
    ['gluteo', 'lumbares'],
    'barra',
    BARRA,
    6,
    10,
  ),
  ex(
    'peso-muerto-sumo',
    'Peso muerto sumo',
    'bisagra',
    ['isquiosurales'],
    ['gluteo', 'aductores'],
    'barra',
    BARRA,
    3,
    6,
  ),
  ex(
    'peso-muerto-mancuernas',
    'Peso muerto con mancuernas',
    'bisagra',
    ['isquiosurales'],
    ['gluteo', 'lumbares'],
    'mancuernas',
    MANCUERNA,
    8,
    12,
  ),
  ex(
    'peso-muerto-piernas-rigidas',
    'Peso muerto a piernas rígidas',
    'bisagra',
    ['isquiosurales'],
    ['gluteo'],
    'barra',
    BARRA,
    8,
    12,
  ),
  ex('hip-thrust', 'Hip thrust', 'bisagra', ['gluteo'], ['isquiosurales'], 'barra', BARRA, 8, 12),
  ex(
    'puente-gluteo',
    'Puente de glúteo',
    'bisagra',
    ['gluteo'],
    ['isquiosurales'],
    'peso_corporal',
    BARRA,
    10,
    20,
  ),
  ex(
    'buenos-dias',
    'Buenos días',
    'bisagra',
    ['isquiosurales'],
    ['lumbares', 'gluteo'],
    'barra',
    BARRA,
    8,
    12,
  ),
  ex(
    'swing-kettlebell',
    'Swing con kettlebell',
    'bisagra',
    ['gluteo'],
    ['isquiosurales', 'lumbares'],
    'kettlebell',
    4,
    10,
    20,
  ),
  ex(
    'patada-gluteo',
    'Patada de glúteo en polea',
    'aislamiento',
    ['gluteo'],
    [],
    'polea',
    MAQUINA,
    12,
    20,
  ),
  ex(
    'hiperextensiones',
    'Hiperextensiones',
    'bisagra',
    ['lumbares'],
    ['gluteo', 'isquiosurales'],
    'peso_corporal',
    MANCUERNA,
    10,
    15,
  ),

  // --- Zancada ---
  ex(
    'zancadas',
    'Zancadas',
    'zancada',
    ['cuadriceps'],
    ['gluteo', 'isquiosurales'],
    'mancuernas',
    MANCUERNA,
    8,
    12,
  ),
  ex(
    'zancadas-caminando',
    'Zancadas caminando',
    'zancada',
    ['cuadriceps'],
    ['gluteo'],
    'mancuernas',
    MANCUERNA,
    10,
    16,
  ),
  ex(
    'sentadilla-bulgara',
    'Sentadilla búlgara',
    'zancada',
    ['cuadriceps'],
    ['gluteo'],
    'mancuernas',
    MANCUERNA,
    8,
    12,
  ),
  ex(
    'step-up',
    'Subida al cajón',
    'zancada',
    ['cuadriceps'],
    ['gluteo'],
    'mancuernas',
    MANCUERNA,
    8,
    12,
  ),
  ex(
    'zancada-inversa',
    'Zancada inversa',
    'zancada',
    ['gluteo'],
    ['cuadriceps'],
    'mancuernas',
    MANCUERNA,
    8,
    12,
  ),

  // --- Aislamiento: bíceps ---
  ex(
    'curl-barra',
    'Curl con barra',
    'aislamiento',
    ['biceps'],
    ['antebrazo'],
    'barra',
    1.25,
    8,
    12,
  ),
  ex(
    'curl-mancuernas',
    'Curl con mancuernas',
    'aislamiento',
    ['biceps'],
    ['antebrazo'],
    'mancuernas',
    MANCUERNA,
    10,
    15,
  ),
  ex(
    'curl-martillo',
    'Curl martillo',
    'aislamiento',
    ['biceps'],
    ['antebrazo'],
    'mancuernas',
    MANCUERNA,
    10,
    15,
  ),
  ex('curl-predicador', 'Curl predicador', 'aislamiento', ['biceps'], [], 'barra', 1.25, 8, 12),
  ex(
    'curl-concentrado',
    'Curl concentrado',
    'aislamiento',
    ['biceps'],
    [],
    'mancuernas',
    MANCUERNA,
    10,
    15,
  ),
  ex(
    'curl-polea',
    'Curl en polea',
    'aislamiento',
    ['biceps'],
    ['antebrazo'],
    'polea',
    MAQUINA,
    10,
    15,
  ),
  ex(
    'curl-inclinado',
    'Curl inclinado',
    'aislamiento',
    ['biceps'],
    [],
    'mancuernas',
    MANCUERNA,
    10,
    15,
  ),

  // --- Aislamiento: tríceps ---
  ex(
    'extension-triceps-polea',
    'Extensión de tríceps en polea',
    'aislamiento',
    ['triceps'],
    [],
    'polea',
    MAQUINA,
    10,
    15,
  ),
  ex('press-frances', 'Press francés', 'aislamiento', ['triceps'], [], 'barra', 1.25, 8, 12),
  ex(
    'extension-triceps-cabeza',
    'Extensión de tríceps sobre la cabeza',
    'aislamiento',
    ['triceps'],
    [],
    'mancuernas',
    MANCUERNA,
    10,
    15,
  ),
  ex(
    'patada-triceps',
    'Patada de tríceps',
    'aislamiento',
    ['triceps'],
    [],
    'mancuernas',
    MANCUERNA,
    12,
    15,
  ),
  ex(
    'fondos-banco',
    'Fondos en banco',
    'aislamiento',
    ['triceps'],
    ['pectoral'],
    'peso_corporal',
    MANCUERNA,
    10,
    15,
  ),
  ex(
    'press-cerrado',
    'Press cerrado',
    'empuje_horizontal',
    ['triceps'],
    ['pectoral'],
    'barra',
    BARRA,
    6,
    10,
  ),

  // --- Aislamiento: hombro ---
  ex(
    'elevaciones-laterales',
    'Elevaciones laterales',
    'aislamiento',
    ['deltoides_lateral'],
    [],
    'mancuernas',
    1,
    12,
    20,
  ),
  ex(
    'elevaciones-laterales-polea',
    'Elevaciones laterales en polea',
    'aislamiento',
    ['deltoides_lateral'],
    [],
    'polea',
    MAQUINA,
    12,
    20,
  ),
  ex(
    'elevaciones-frontales',
    'Elevaciones frontales',
    'aislamiento',
    ['deltoides_anterior'],
    [],
    'mancuernas',
    1,
    12,
    20,
  ),
  ex(
    'pajaros',
    'Pájaros',
    'aislamiento',
    ['deltoides_posterior'],
    ['trapecio'],
    'mancuernas',
    1,
    12,
    20,
  ),
  ex(
    'pajaros-maquina',
    'Deltoides posterior en máquina',
    'aislamiento',
    ['deltoides_posterior'],
    [],
    'maquina',
    MAQUINA,
    12,
    20,
  ),
  ex(
    'encogimientos',
    'Encogimientos de hombros',
    'aislamiento',
    ['trapecio'],
    ['antebrazo'],
    'mancuernas',
    MANCUERNA,
    10,
    15,
  ),
  ex(
    'encogimientos-barra',
    'Encogimientos con barra',
    'aislamiento',
    ['trapecio'],
    ['antebrazo'],
    'barra',
    BARRA,
    10,
    15,
  ),

  // --- Aislamiento: pierna ---
  ex(
    'extension-cuadriceps',
    'Extensión de cuádriceps',
    'aislamiento',
    ['cuadriceps'],
    [],
    'maquina',
    MAQUINA,
    10,
    15,
  ),
  ex(
    'curl-femoral-tumbado',
    'Curl femoral tumbado',
    'aislamiento',
    ['isquiosurales'],
    [],
    'maquina',
    MAQUINA,
    10,
    15,
  ),
  ex(
    'curl-femoral-sentado',
    'Curl femoral sentado',
    'aislamiento',
    ['isquiosurales'],
    [],
    'maquina',
    MAQUINA,
    10,
    15,
  ),
  ex(
    'curl-nordico',
    'Curl nórdico',
    'aislamiento',
    ['isquiosurales'],
    [],
    'peso_corporal',
    MANCUERNA,
    5,
    10,
  ),
  ex(
    'elevacion-talones-pie',
    'Elevación de talones de pie',
    'aislamiento',
    ['gemelo'],
    [],
    'maquina',
    MAQUINA,
    12,
    20,
  ),
  ex(
    'elevacion-talones-sentado',
    'Elevación de talones sentado',
    'aislamiento',
    ['gemelo'],
    [],
    'maquina',
    MAQUINA,
    12,
    20,
  ),
  ex(
    'abductores-maquina',
    'Abductores en máquina',
    'aislamiento',
    ['gluteo'],
    [],
    'maquina',
    MAQUINA,
    12,
    20,
  ),
  ex(
    'aductores-maquina',
    'Aductores en máquina',
    'aislamiento',
    ['aductores'],
    [],
    'maquina',
    MAQUINA,
    12,
    20,
  ),

  // --- Aislamiento: antebrazo ---
  ex('curl-muneca', 'Curl de muñeca', 'aislamiento', ['antebrazo'], [], 'mancuernas', 1, 12, 20),
  ex(
    'paseo-granjero',
    'Paseo del granjero',
    'aislamiento',
    ['antebrazo'],
    ['trapecio', 'abdominales'],
    'mancuernas',
    MANCUERNA,
    1,
    3,
  ),

  // --- Core ---
  ex('plancha', 'Plancha', 'core', ['abdominales'], ['lumbares'], 'peso_corporal', MANCUERNA, 1, 3),
  ex(
    'plancha-lateral',
    'Plancha lateral',
    'core',
    ['abdominales'],
    ['lumbares'],
    'peso_corporal',
    MANCUERNA,
    1,
    3,
  ),
  ex(
    'rueda-abdominal',
    'Rueda abdominal',
    'core',
    ['abdominales'],
    ['dorsal'],
    'peso_corporal',
    MANCUERNA,
    6,
    12,
  ),
  ex('crunch', 'Crunch abdominal', 'core', ['abdominales'], [], 'peso_corporal', MANCUERNA, 12, 20),
  ex('crunch-polea', 'Crunch en polea', 'core', ['abdominales'], [], 'polea', MAQUINA, 10, 15),
  ex(
    'elevacion-piernas',
    'Elevación de piernas colgado',
    'core',
    ['abdominales'],
    ['antebrazo'],
    'peso_corporal',
    MANCUERNA,
    8,
    15,
  ),
  ex(
    'elevacion-rodillas',
    'Elevación de rodillas',
    'core',
    ['abdominales'],
    [],
    'peso_corporal',
    MANCUERNA,
    10,
    20,
  ),
  ex('russian-twist', 'Giro ruso', 'core', ['abdominales'], [], 'disco', 1.25, 12, 20),
  ex('pallof-press', 'Press Pallof', 'core', ['abdominales'], [], 'polea', MAQUINA, 10, 15),
  ex('hollow-hold', 'Hollow hold', 'core', ['abdominales'], [], 'peso_corporal', MANCUERNA, 1, 3),
  ex(
    'bicho-muerto',
    'Bicho muerto',
    'core',
    ['abdominales'],
    [],
    'peso_corporal',
    MANCUERNA,
    10,
    20,
  ),
  ex('superman', 'Superman', 'core', ['lumbares'], ['gluteo'], 'peso_corporal', MANCUERNA, 10, 20),
  ex(
    'ab-wheel-rodillas',
    'Rueda abdominal de rodillas',
    'core',
    ['abdominales'],
    [],
    'peso_corporal',
    MANCUERNA,
    8,
    15,
  ),

  // --- Variantes frecuentes que completan los patrones ---
  ex(
    'press-banca-agarre-ancho',
    'Press de banca agarre ancho',
    'empuje_horizontal',
    ['pectoral'],
    ['deltoides_anterior'],
    'barra',
    BARRA,
    6,
    10,
  ),
  ex(
    'press-banca-smith',
    'Press de banca en multipower',
    'empuje_horizontal',
    ['pectoral'],
    ['triceps'],
    'maquina',
    MAQUINA,
    8,
    12,
  ),
  ex(
    'sentadilla-smith',
    'Sentadilla en multipower',
    'sentadilla',
    ['cuadriceps'],
    ['gluteo'],
    'maquina',
    MAQUINA,
    8,
    12,
  ),
  ex(
    'remo-gironda',
    'Remo Gironda',
    'traccion_horizontal',
    ['dorsal'],
    ['biceps'],
    'polea',
    MAQUINA,
    8,
    12,
  ),
  ex(
    'jalon-tras-nuca',
    'Jalón tras nuca',
    'traccion_vertical',
    ['dorsal'],
    ['biceps'],
    'polea',
    MAQUINA,
    10,
    15,
  ),
  ex(
    'remo-mancuernas-banco',
    'Remo con mancuernas en banco',
    'traccion_horizontal',
    ['dorsal'],
    ['biceps', 'deltoides_posterior'],
    'mancuernas',
    MANCUERNA,
    8,
    12,
  ),
  ex(
    'press-mancuernas-neutro',
    'Press con mancuernas agarre neutro',
    'empuje_horizontal',
    ['pectoral'],
    ['triceps'],
    'mancuernas',
    MANCUERNA,
    8,
    12,
  ),
  ex(
    'peso-muerto-trap-bar',
    'Peso muerto con barra hexagonal',
    'bisagra',
    ['cuadriceps'],
    ['gluteo', 'isquiosurales', 'trapecio'],
    'barra',
    BARRA,
    5,
    8,
  ),
  ex(
    'remo-unilateral-polea',
    'Remo unilateral en polea',
    'traccion_horizontal',
    ['dorsal'],
    ['biceps'],
    'polea',
    MAQUINA,
    10,
    15,
  ),
  ex(
    'press-landmine',
    'Press landmine',
    'empuje_inclinado',
    ['deltoides_anterior'],
    ['pectoral', 'triceps'],
    'barra',
    BARRA,
    8,
    12,
  ),
  ex(
    'remo-landmine',
    'Remo landmine',
    'traccion_horizontal',
    ['dorsal'],
    ['biceps', 'trapecio'],
    'barra',
    BARRA,
    8,
    12,
  ),
  ex(
    'goblet-lateral',
    'Sentadilla lateral con mancuerna',
    'zancada',
    ['aductores'],
    ['cuadriceps', 'gluteo'],
    'mancuernas',
    MANCUERNA,
    10,
    15,
  ),
  ex(
    'elevacion-talones-prensa',
    'Elevación de talones en prensa',
    'aislamiento',
    ['gemelo'],
    [],
    'maquina',
    MAQUINA,
    12,
    20,
  ),
  ex('curl-araña', 'Curl araña', 'aislamiento', ['biceps'], [], 'mancuernas', MANCUERNA, 10, 15),
  ex(
    'extension-triceps-cuerda',
    'Extensión de tríceps con cuerda',
    'aislamiento',
    ['triceps'],
    [],
    'polea',
    MAQUINA,
    12,
    20,
  ),
  ex(
    'remo-al-menton',
    'Remo al mentón',
    'aislamiento',
    ['deltoides_lateral'],
    ['trapecio', 'biceps'],
    'barra',
    1.25,
    10,
    15,
  ),
  ex(
    'press-banca-inclinado-smith',
    'Press inclinado en multipower',
    'empuje_inclinado',
    ['pectoral'],
    ['deltoides_anterior'],
    'maquina',
    MAQUINA,
    8,
    12,
  ),
  ex(
    'dominadas-lastradas',
    'Dominadas lastradas',
    'traccion_vertical',
    ['dorsal'],
    ['biceps'],
    'disco',
    1.25,
    4,
    8,
  ),
  ex(
    'fondos-lastrados',
    'Fondos lastrados',
    'empuje_horizontal',
    ['pectoral'],
    ['triceps'],
    'disco',
    1.25,
    5,
    10,
  ),
  ex(
    'sentadilla-copa-kettlebell',
    'Sentadilla con kettlebell',
    'sentadilla',
    ['cuadriceps'],
    ['gluteo'],
    'kettlebell',
    4,
    10,
    15,
  ),
  ex(
    'peso-muerto-una-pierna',
    'Peso muerto a una pierna',
    'bisagra',
    ['isquiosurales'],
    ['gluteo'],
    'mancuernas',
    MANCUERNA,
    8,
    12,
  ),
  ex(
    'press-pecho-polea',
    'Press de pecho en polea',
    'empuje_horizontal',
    ['pectoral'],
    ['triceps'],
    'polea',
    MAQUINA,
    10,
    15,
  ),
];

const BY_ID = new Map(EXERCISES.map((e) => [e.id, e]));

export function findExercise(id: string): Exercise | undefined {
  return BY_ID.get(id);
}
